/**
 * Utility to suppress common harmless errors that create noise in the console
 */

/**
 * Suppress ResizeObserver errors which are common in React apps with UI libraries
 * These errors are harmless but create console noise
 */
export const suppressResizeObserverErrors = () => {
  // Store the original console.error
  const originalConsoleError = console.error;

  // Override console.error to filter out ResizeObserver errors
  console.error = (...args) => {
    // Check if any argument contains the ResizeObserver error message
    const hasResizeObserverError = args.some(
      (arg) =>
        typeof arg === "string" &&
        arg.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
    );

    // If it's not a ResizeObserver error, log it normally
    if (!hasResizeObserverError) {
      originalConsoleError.apply(console, args);
    }
  };
};

/**
 * Suppress specific window error events that are harmless
 */
export const suppressHarmlessWindowErrors = () => {
  window.addEventListener(
    "error",
    (event) => {
      // Suppress ResizeObserver errors
      if (
        event.message &&
        event.message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        )
      ) {
        event.preventDefault();
        return false;
      }

      // IMPORTANT: Only suppress truly harmless errors
      // DO NOT suppress "Script error" or "Network request failed" as these indicate real issues
      const harmlessErrors = [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured with value: undefined", // Only specific non-errors
      ];

      const isHarmless = harmlessErrors.some(
        (errorMsg) => event.message && event.message.includes(errorMsg)
      );

      if (isHarmless) {
        event.preventDefault();
        return false;
      }

      // All other errors should be logged normally for debugging
      // This includes: Script errors, Network errors, etc.
    },
    true
  );
};

/**
 * Polyfill ResizeObserver to handle errors gracefully
 * This is specifically useful for Ant Design components
 */
export const polyfillResizeObserver = () => {
  if (typeof window !== "undefined" && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;

    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          try {
            callback(entries, observer);
          } catch (error) {
            // Suppress ResizeObserver errors
            if (
              error.message &&
              error.message.includes("ResizeObserver loop completed")
            ) {
              return;
            }
            throw error;
          }
        });
      }
    };
  }
};

/**
 * More aggressive ResizeObserver error suppression
 * This handles the error at the source before it can bubble up
 */
export const suppressResizeObserverAtSource = () => {
  // Monkey patch the ResizeObserver constructor
  if (typeof window !== "undefined" && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;

    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries, observer) => {
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              // Completely suppress ResizeObserver errors
              if (
                error.message &&
                error.message.includes("ResizeObserver loop completed")
              ) {
                return;
              }
              throw error;
            }
          });
        };
        super(wrappedCallback);
      }
    };
  }
};

/**
 * Suppress React error boundary from catching ResizeObserver errors
 */
export const suppressReactErrorBoundary = () => {
  // Override the global error handler that React uses
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (
      typeof message === "string" &&
      message.includes("ResizeObserver loop completed")
    ) {
      return true; // Prevent the error from being handled
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Also handle unhandled promise rejections
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    if (
      event.reason &&
      event.reason.message &&
      event.reason.message.includes("ResizeObserver loop completed")
    ) {
      event.preventDefault();
      return;
    }
    if (originalOnUnhandledRejection) {
      originalOnUnhandledRejection(event);
    }
  };
};

/**
 * Initialize all error suppression
 */
export const initializeErrorSuppression = () => {
  suppressResizeObserverErrors();
  suppressHarmlessWindowErrors();
  polyfillResizeObserver();
  suppressResizeObserverAtSource();
  suppressReactErrorBoundary();
};
